import React, { useState, useEffect, useCallback } from 'react';
import { LogOut, Users, Phone, FileText, MessageCircle, Loader2, Trash2, Eye, Filter, Save, Plus, X, LayoutDashboard, Type, DollarSign, HelpCircle, Quote, Scale, Briefcase, Grid3x3, TrendingUp, KeyRound } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { invalidateCMS } from '../hooks/useCMS';
import { useI18n } from '../i18n/I18nProvider';
import { translateApiError } from '../i18n/apiError';

const API = process.env.REACT_APP_BACKEND_URL;

/* ─── Login ─── */
function LoginForm({ onLogin }) {
  const { t } = useI18n();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/auth/login`, { email, password });
      onLogin(data.token);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(translateApiError(t, detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#f8f8fa] px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="h-10 w-10 rounded-xl bg-[#0b0b0f] flex items-center justify-center mx-auto">
            <span className="text-white font-extrabold text-xl leading-none">b</span>
          </div>
          <h1 className="font-display text-2xl mt-4 text-[#0b0b0f]">{t('admin.title')}</h1>
          <p className="text-[#6b6b73] mt-1 text-sm">{t('admin.loginSubtitle')}</p>
        </div>
        <form onSubmit={submit} className="bg-white rounded-2xl border border-black/5 shadow-sm p-6 space-y-4" data-testid="admin-login-form">
          <div>
            <label className="text-sm font-medium text-[#3a3a42]">{t('admin.email')}</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 w-full rounded-xl border border-black/10 px-4 py-3 text-[15px] outline-none focus:border-[#76B828] focus:ring-2 focus:ring-[#76B828]/20 transition" placeholder="hello@wearebookers.com" data-testid="admin-email" />
          </div>
          <div>
            <label className="text-sm font-medium text-[#3a3a42]">{t('admin.password')}</label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 w-full rounded-xl border border-black/10 px-4 py-3 text-[15px] outline-none focus:border-[#76B828] focus:ring-2 focus:ring-[#76B828]/20 transition" data-testid="admin-password" />
          </div>
          <button type="submit" disabled={loading} className="btn-pill btn-dark w-full py-3 text-[15px] disabled:opacity-70" data-testid="admin-login-btn">
            {loading ? <Loader2 size={18} className="animate-spin" /> : t('admin.signIn')}
          </button>
        </form>
      </div>
    </div>
  );
}

/* ─── Shared ─── */
const STATUS_COLORS = {
  new: 'bg-blue-50 text-blue-700 border-blue-200',
  contacted: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  converted: 'bg-green-50 text-green-700 border-green-200',
  archived: 'bg-gray-50 text-gray-500 border-gray-200',
};
const TYPE_ICONS = { contact: MessageCircle, signup: FileText, callback: Phone };
const TYPE_LABELS = { contact: 'Contact', signup: 'D\u00e9mo', callback: 'Rappel' };

const inputCls = "w-full rounded-xl border border-black/10 px-4 py-2.5 text-[14px] outline-none focus:border-[#76B828] focus:ring-2 focus:ring-[#76B828]/20 transition bg-white";
const labelCls = "text-sm font-medium text-[#3a3a42] block mb-1";

function SaveBtn({ loading, onClick }) {
  const { t } = useI18n();
  return (
    <button onClick={onClick} disabled={loading} className="btn-pill btn-dark px-6 py-2.5 text-sm disabled:opacity-60" data-testid="cms-save-btn">
      {loading ? <Loader2 size={16} className="animate-spin" /> : <><Save size={15} /> {t('admin.save')}</>}
    </button>
  );
}

/* ─── Leads Tab ─── */
function LeadsTab({ token, onLogout }) {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    try {
      const [lr, sr] = await Promise.all([
        axios.get(`${API}/api/admin/leads`, { headers }),
        axios.get(`${API}/api/admin/stats`, { headers }),
      ]);
      setLeads(lr.data); setStats(sr.data);
    } catch (err) { if (err.response?.status === 401) onLogout(); }
    finally { setLoading(false); }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchData(); }, [fetchData]);

  const updateStatus = async (id, s) => {
    try {
      await axios.patch(`${API}/api/admin/leads/${id}`, { status: s }, { headers });
      setLeads(p => p.map(l => l._id === id ? { ...l, status: s } : l));
      toast.success('Statut mis \u00e0 jour');
    } catch { toast.error('Erreur'); }
  };
  const deleteLead = async (id) => {
    if (!window.confirm('Supprimer ce lead ?')) return;
    try {
      await axios.delete(`${API}/api/admin/leads/${id}`, { headers });
      setLeads(p => p.filter(l => l._id !== id));
      toast.success('Lead supprim\u00e9');
    } catch { toast.error('Erreur'); }
  };

  const filtered = filter === 'all' ? leads : leads.filter(l => l.type === filter);
  if (loading) return <div className="flex justify-center py-20"><Loader2 size={28} className="animate-spin text-[#76B828]" /></div>;

  return (
    <>
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8" data-testid="admin-stats">
          {[
            { label: 'Total', value: stats.total, icon: Users, color: 'bg-[#EEF6DC] text-[#76B828]' },
            { label: 'Nouveaux', value: stats.new_leads, icon: Eye, color: 'bg-blue-50 text-blue-600' },
            { label: 'Contacts', value: stats.contacts, icon: MessageCircle, color: 'bg-orange-50 text-orange-600' },
            { label: 'D\u00e9mos', value: stats.signups, icon: FileText, color: 'bg-green-50 text-green-600' },
            { label: 'Rappels', value: stats.callbacks, icon: Phone, color: 'bg-purple-50 text-purple-600' },
          ].map(s => (
            <div key={s.label} className="bg-white rounded-2xl border border-black/5 p-5">
              <div className={`h-9 w-9 rounded-lg ${s.color} flex items-center justify-center mb-3`}><s.icon size={18} /></div>
              <p className="font-display text-2xl text-[#0b0b0f]">{s.value}</p>
              <p className="text-[#8b8b95] text-sm mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>
      )}
      <div className="flex items-center gap-2 mb-6 flex-wrap" data-testid="admin-filters">
        <Filter size={16} className="text-[#8b8b95]" />
        {[{ key: 'all', label: 'Tous' }, { key: 'contact', label: 'Contacts' }, { key: 'signup', label: 'D\u00e9mos' }, { key: 'callback', label: 'Rappels' }].map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)} className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${filter === f.key ? 'bg-[#0b0b0f] text-white' : 'bg-white border border-black/10 text-[#3a3a42] hover:bg-black/5'}`} data-testid={`filter-${f.key}`}>{f.label}</button>
        ))}
      </div>
      {filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-black/5 p-12 text-center">
          <Users size={40} className="mx-auto text-[#d1d1d5]" /><p className="text-[#8b8b95] mt-4">Aucun lead</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="admin-leads-list">
          {filtered.map(lead => {
            const Icon = TYPE_ICONS[lead.type] || MessageCircle;
            const exp = expandedId === lead._id;
            return (
              <div key={lead._id} className="bg-white rounded-2xl border border-black/5 overflow-hidden hover:shadow-sm transition-shadow">
                <div className="flex items-center gap-4 px-5 py-4 cursor-pointer" onClick={() => setExpandedId(exp ? null : lead._id)}>
                  <div className="h-10 w-10 shrink-0 rounded-xl bg-[#EEF6DC] flex items-center justify-center"><Icon size={18} className="text-[#76B828]" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-[15px] text-[#0b0b0f]">{lead.prenom || lead.name || ''} {lead.nom || ''}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${STATUS_COLORS[lead.status] || STATUS_COLORS.new}`}>{lead.status || 'new'}</span>
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#EEF6DC] text-[#76B828]">{TYPE_LABELS[lead.type] || lead.type}</span>
                    </div>
                    <p className="text-sm text-[#8b8b95] mt-0.5 truncate">{lead.restaurant_name && `${lead.restaurant_name} \u00b7 `}{lead.telephone || ''}</p>
                  </div>
                  <span className="text-xs text-[#8b8b95] shrink-0">{lead.created_at ? new Date(lead.created_at).toLocaleDateString('fr-CH') : ''}</span>
                </div>
                {exp && (
                  <div className="px-5 pb-5 border-t border-black/5 pt-4">
                    <div className="grid sm:grid-cols-2 gap-3 text-sm">
                      {lead.restaurant_name && <div><span className="text-[#8b8b95]">Restaurant:</span> <span className="font-medium">{lead.restaurant_name}</span></div>}
                      {lead.telephone && <div><span className="text-[#8b8b95]">{`T\u00e9l:`}</span> <span className="font-medium">{lead.telephone}</span></div>}
                      {lead.address && <div><span className="text-[#8b8b95]">Adresse:</span> <span className="font-medium">{lead.address}</span></div>}
                      {lead.message && <div className="sm:col-span-2"><span className="text-[#8b8b95]">Message:</span> <span className="font-medium">{lead.message}</span></div>}
                    </div>
                    <div className="flex items-center gap-2 mt-4 flex-wrap">
                      {['new', 'contacted', 'converted', 'archived'].map(s => (
                        <button key={s} onClick={() => updateStatus(lead._id, s)} className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${lead.status === s ? STATUS_COLORS[s] : 'bg-white border-black/10 text-[#6b6b73] hover:bg-black/5'}`}>{s}</button>
                      ))}
                      <button onClick={() => deleteLead(lead._id)} className="ml-auto p-2 rounded-lg hover:bg-red-50 text-red-500 transition-colors"><Trash2 size={16} /></button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

/* ─── CMS Editor Hook ─── */
function useCmsEditor(token, section) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/api/cms/${section}`, { headers })
      .then(r => setData(r.data?.content || null))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [section]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (content) => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/cms/${section}`, { content }, { headers });
      setData(content);
      invalidateCMS();
      toast.success('Section sauvegard\u00e9e !');
    } catch { toast.error('Erreur de sauvegarde'); }
    finally { setSaving(false); }
  };

  return { data, loading, saving, save, setData };
}

/* ─── Hero Editor ─── */
function HeroEditor({ token }) {
  const { data, loading, saving, save } = useCmsEditor(token, 'hero');
  const [form, setForm] = useState(null);

  useEffect(() => { if (data && !form) setForm({ ...data }); }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading || !form) return <div className="flex justify-center py-20"><Loader2 size={28} className="animate-spin text-[#76B828]" /></div>;

  const u = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="space-y-6" data-testid="cms-hero-editor">
      <div className="bg-white rounded-2xl border border-black/5 p-6 space-y-4">
        <h3 className="font-display text-lg text-[#0b0b0f]">Hero Section</h3>
        <div><label className={labelCls}>Eyebrow</label><input value={form.eyebrow || ''} onChange={u('eyebrow')} className={inputCls} /></div>
        <div><label className={labelCls}>Title</label><input value={form.title || ''} onChange={u('title')} className={inputCls} /></div>
        <div><label className={labelCls}>Description</label><textarea value={form.description || ''} onChange={u('description')} rows={3} className={inputCls + ' resize-none'} /></div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className={labelCls}>Badge rating</label><input value={form.badge_rating || ''} onChange={u('badge_rating')} className={inputCls} /></div>
          <div><label className={labelCls}>CTA Primary</label><input value={form.cta_primary || ''} onChange={u('cta_primary')} className={inputCls} /></div>
        </div>
        <div><label className={labelCls}>CTA Secondary</label><input value={form.cta_secondary || ''} onChange={u('cta_secondary')} className={inputCls} /></div>
      </div>
      <div className="flex justify-end"><SaveBtn loading={saving} onClick={() => save(form)} /></div>
    </div>
  );
}

/* ─── Pricing Editor ─── */
function PricingEditor({ token }) {
  const { data, loading, saving, save } = useCmsEditor(token, 'pricing');
  const [form, setForm] = useState(null);

  useEffect(() => { if (data && !form) setForm(JSON.parse(JSON.stringify(data))); }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading || !form) return <div className="flex justify-center py-20"><Loader2 size={28} className="animate-spin text-[#76B828]" /></div>;

  const u = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const uPkg = (idx, k) => (e) => {
    const pkgs = [...form.packages];
    pkgs[idx] = { ...pkgs[idx], [k]: k === 'popular' ? e.target.checked : e.target.value };
    setForm({ ...form, packages: pkgs });
  };
  const uFeature = (pkgIdx, fIdx) => (e) => {
    const pkgs = JSON.parse(JSON.stringify(form.packages));
    pkgs[pkgIdx].features[fIdx] = e.target.value;
    setForm({ ...form, packages: pkgs });
  };
  const addFeature = (pkgIdx) => {
    const pkgs = JSON.parse(JSON.stringify(form.packages));
    pkgs[pkgIdx].features.push('');
    setForm({ ...form, packages: pkgs });
  };
  const removeFeature = (pkgIdx, fIdx) => {
    const pkgs = JSON.parse(JSON.stringify(form.packages));
    pkgs[pkgIdx].features.splice(fIdx, 1);
    setForm({ ...form, packages: pkgs });
  };

  return (
    <div className="space-y-6" data-testid="cms-pricing-editor">
      <div className="bg-white rounded-2xl border border-black/5 p-6 space-y-4">
        <h3 className="font-display text-lg text-[#0b0b0f]">Pricing Header</h3>
        <div><label className={labelCls}>Eyebrow</label><input value={form.eyebrow || ''} onChange={u('eyebrow')} className={inputCls} /></div>
        <div><label className={labelCls}>Title</label><input value={form.title || ''} onChange={u('title')} className={inputCls} /></div>
        <div><label className={labelCls}>Description</label><textarea value={form.description || ''} onChange={u('description')} rows={2} className={inputCls + ' resize-none'} /></div>
        <div><label className={labelCls}>Footer text</label><textarea value={form.footer_text || ''} onChange={u('footer_text')} rows={2} className={inputCls + ' resize-none'} /></div>
      </div>

      {(form.packages || []).map((pkg, pi) => (
        <div key={pi} className="bg-white rounded-2xl border border-black/5 p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-lg text-[#0b0b0f]">Package {pi + 1}: {pkg.name}</h3>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={pkg.popular || false} onChange={uPkg(pi, 'popular')} className="rounded" />
              Popular
            </label>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className={labelCls}>Name</label><input value={pkg.name || ''} onChange={uPkg(pi, 'name')} className={inputCls} /></div>
            <div><label className={labelCls}>Description</label><input value={pkg.desc || ''} onChange={uPkg(pi, 'desc')} className={inputCls} /></div>
          </div>
          <div className="grid sm:grid-cols-4 gap-4">
            <div><label className={labelCls}>Price (CHF)</label><input value={pkg.price || ''} onChange={uPkg(pi, 'price')} className={inputCls} /></div>
            <div><label className={labelCls}>Original</label><input value={pkg.original || ''} onChange={uPkg(pi, 'original')} className={inputCls} /></div>
            <div><label className={labelCls}>Tag</label><input value={pkg.tag || ''} onChange={uPkg(pi, 'tag')} className={inputCls} /></div>
            <div><label className={labelCls}>Offer label</label><input value={pkg.offer || ''} onChange={uPkg(pi, 'offer')} className={inputCls} /></div>
          </div>
          <div>
            <label className={labelCls}>Features</label>
            <div className="space-y-2">
              {(pkg.features || []).map((f, fi) => (
                <div key={fi} className="flex gap-2">
                  <input value={f} onChange={uFeature(pi, fi)} className={inputCls} />
                  <button onClick={() => removeFeature(pi, fi)} className="shrink-0 p-2 rounded-lg hover:bg-red-50 text-red-400"><X size={16} /></button>
                </div>
              ))}
              <button onClick={() => addFeature(pi)} className="flex items-center gap-1 text-sm text-[#76B828] font-medium hover:underline"><Plus size={14} /> Add feature</button>
            </div>
          </div>
        </div>
      ))}
      <div className="flex justify-end"><SaveBtn loading={saving} onClick={() => save(form)} /></div>
    </div>
  );
}

/* ─── FAQ Editor ─── */
function FAQEditor({ token }) {
  const { data, loading, saving, save } = useCmsEditor(token, 'faq');
  const [form, setForm] = useState(null);

  useEffect(() => { if (data && !form) setForm(JSON.parse(JSON.stringify(data))); }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading || !form) return <div className="flex justify-center py-20"><Loader2 size={28} className="animate-spin text-[#76B828]" /></div>;

  const u = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const uItem = (i, k) => (e) => {
    const items = [...form.items];
    items[i] = { ...items[i], [k]: e.target.value };
    setForm({ ...form, items });
  };
  const addItem = () => setForm({ ...form, items: [...form.items, { q: '', a: '', q_fr: '', a_fr: '' }] });
  const removeItem = (i) => {
    const items = [...form.items];
    items.splice(i, 1);
    setForm({ ...form, items });
  };

  return (
    <div className="space-y-6" data-testid="cms-faq-editor">
      <div className="bg-white rounded-2xl border border-black/5 p-6 space-y-4">
        <h3 className="font-display text-lg text-[#0b0b0f]">FAQ Header</h3>
        <div><label className={labelCls}>Title (EN)</label><input value={form.title || ''} onChange={u('title')} className={inputCls} /></div>
        <div><label className={labelCls}>Title (FR)</label><input value={form.title_fr || ''} onChange={u('title_fr')} className={inputCls} /></div>
        <div><label className={labelCls}>Description (EN)</label><textarea value={form.description || ''} onChange={u('description')} rows={2} className={inputCls + ' resize-none'} /></div>
        <div><label className={labelCls}>Description (FR)</label><textarea value={form.description_fr || ''} onChange={u('description_fr')} rows={2} className={inputCls + ' resize-none'} /></div>
      </div>

      {(form.items || []).map((item, i) => (
        <div key={i} className="bg-white rounded-2xl border border-black/5 p-6 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-[#8b8b95]">FAQ {i + 1}</span>
            <button onClick={() => removeItem(i)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-400"><X size={16} /></button>
          </div>
          <div><label className={labelCls}>Question (EN)</label><input value={item.q || ''} onChange={uItem(i, 'q')} className={inputCls} /></div>
          <div><label className={labelCls}>Answer (EN)</label><textarea value={item.a || ''} onChange={uItem(i, 'a')} rows={3} className={inputCls + ' resize-none'} /></div>
          <div><label className={labelCls}>Question (FR)</label><input value={item.q_fr || ''} onChange={uItem(i, 'q_fr')} className={inputCls} /></div>
          <div><label className={labelCls}>Answer (FR)</label><textarea value={item.a_fr || ''} onChange={uItem(i, 'a_fr')} rows={3} className={inputCls + ' resize-none'} /></div>
        </div>
      ))}
      <button onClick={addItem} className="flex items-center gap-1 text-sm text-[#76B828] font-medium hover:underline"><Plus size={14} /> Add FAQ item</button>
      <div className="flex justify-end"><SaveBtn loading={saving} onClick={() => save(form)} /></div>
    </div>
  );
}

/* ─── Testimonials Editor ─── */
function TestimonialsEditor({ token }) {
  const { data, loading, saving, save } = useCmsEditor(token, 'testimonials');
  const [form, setForm] = useState(null);

  useEffect(() => { if (data && !form) setForm(JSON.parse(JSON.stringify(data))); }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading || !form) return <div className="flex justify-center py-20"><Loader2 size={28} className="animate-spin text-[#76B828]" /></div>;

  const uFt = (i, k) => (e) => {
    const arr = [...form.feature_testimonials];
    arr[i] = { ...arr[i], [k]: e.target.value };
    setForm({ ...form, feature_testimonials: arr });
  };
  const uSt = (i, k) => (e) => {
    const arr = [...form.short_testimonials];
    arr[i] = { ...arr[i], [k]: e.target.value };
    setForm({ ...form, short_testimonials: arr });
  };
  const addFt = () => setForm({ ...form, feature_testimonials: [...form.feature_testimonials, { name: '', company: '', role: '', quote: '', image: '' }] });
  const removeFt = (i) => { const arr = [...form.feature_testimonials]; arr.splice(i, 1); setForm({ ...form, feature_testimonials: arr }); };
  const addSt = () => setForm({ ...form, short_testimonials: [...form.short_testimonials, { title: '', quote: '', name: '', role: '' }] });
  const removeSt = (i) => { const arr = [...form.short_testimonials]; arr.splice(i, 1); setForm({ ...form, short_testimonials: arr }); };

  return (
    <div className="space-y-6" data-testid="cms-testimonials-editor">
      <h3 className="font-display text-lg text-[#0b0b0f]">Featured Testimonials (with photo)</h3>
      {(form.feature_testimonials || []).map((ft, i) => (
        <div key={i} className="bg-white rounded-2xl border border-black/5 p-6 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-[#8b8b95]">Featured {i + 1}</span>
            <button onClick={() => removeFt(i)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-400"><X size={16} /></button>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <div><label className={labelCls}>Name</label><input value={ft.name || ''} onChange={uFt(i, 'name')} className={inputCls} /></div>
            <div><label className={labelCls}>Company</label><input value={ft.company || ''} onChange={uFt(i, 'company')} className={inputCls} /></div>
            <div><label className={labelCls}>Role</label><input value={ft.role || ''} onChange={uFt(i, 'role')} className={inputCls} /></div>
          </div>
          <div><label className={labelCls}>Quote</label><textarea value={ft.quote || ''} onChange={uFt(i, 'quote')} rows={2} className={inputCls + ' resize-none'} /></div>
          <div><label className={labelCls}>Image URL</label><input value={ft.image || ''} onChange={uFt(i, 'image')} className={inputCls} /></div>
        </div>
      ))}
      <button onClick={addFt} className="flex items-center gap-1 text-sm text-[#76B828] font-medium hover:underline"><Plus size={14} /> Add featured testimonial</button>

      <h3 className="font-display text-lg text-[#0b0b0f] pt-4">Short Testimonials</h3>
      {(form.short_testimonials || []).map((st, i) => (
        <div key={i} className="bg-white rounded-2xl border border-black/5 p-6 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-[#8b8b95]">Short {i + 1}</span>
            <button onClick={() => removeSt(i)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-400"><X size={16} /></button>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <div><label className={labelCls}>Title</label><input value={st.title || ''} onChange={uSt(i, 'title')} className={inputCls} /></div>
            <div><label className={labelCls}>Name</label><input value={st.name || ''} onChange={uSt(i, 'name')} className={inputCls} /></div>
            <div><label className={labelCls}>Role</label><input value={st.role || ''} onChange={uSt(i, 'role')} className={inputCls} /></div>
          </div>
          <div><label className={labelCls}>Quote</label><textarea value={st.quote || ''} onChange={uSt(i, 'quote')} rows={2} className={inputCls + ' resize-none'} /></div>
        </div>
      ))}
      <button onClick={addSt} className="flex items-center gap-1 text-sm text-[#76B828] font-medium hover:underline"><Plus size={14} /> Add short testimonial</button>
      <div className="flex justify-end"><SaveBtn loading={saving} onClick={() => save(form)} /></div>
    </div>
  );
}

/* ─── Legal Editor ─── */
function LegalEditor({ token }) {
  const sections = [
    { key: 'legal_terms', label: "Conditions g\u00e9n\u00e9rales" },
    { key: 'legal_privacy', label: "Confidentialit\u00e9" },
    { key: 'legal_cookies', label: "Cookies" },
  ];
  const [active, setActive] = useState('legal_terms');

  return (
    <div data-testid="cms-legal-editor">
      <div className="flex gap-2 mb-6">
        {sections.map(s => (
          <button key={s.key} onClick={() => setActive(s.key)} className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${active === s.key ? 'bg-[#76B828] text-white' : 'bg-white border border-black/10 text-[#3a3a42] hover:bg-black/5'}`}>{s.label}</button>
        ))}
      </div>
      <LegalSectionEditor key={active} token={token} section={active} />
    </div>
  );
}

function LegalSectionEditor({ token, section }) {
  const { data, loading, saving, save } = useCmsEditor(token, section);
  const [form, setForm] = useState(null);

  useEffect(() => { if (data && !form) setForm(JSON.parse(JSON.stringify(data))); }, [data]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setForm(null); }, [section]);

  if (loading || !form) return <div className="flex justify-center py-20"><Loader2 size={28} className="animate-spin text-[#76B828]" /></div>;

  const u = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const uSec = (i, k) => (e) => {
    const secs = [...form.sections];
    secs[i] = { ...secs[i], [k]: e.target.value };
    setForm({ ...form, sections: secs });
  };
  const addSec = () => setForm({ ...form, sections: [...form.sections, { heading: '', body: '' }] });
  const removeSec = (i) => { const secs = [...form.sections]; secs.splice(i, 1); setForm({ ...form, sections: secs }); };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl border border-black/5 p-6 space-y-4">
        <div><label className={labelCls}>Title</label><input value={form.title || ''} onChange={u('title')} className={inputCls} /></div>
        <div><label className={labelCls}>Last updated</label><input value={form.last_updated || ''} onChange={u('last_updated')} className={inputCls} /></div>
      </div>
      {(form.sections || []).map((sec, i) => (
        <div key={i} className="bg-white rounded-2xl border border-black/5 p-6 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-[#8b8b95]">Section {i + 1}</span>
            <button onClick={() => removeSec(i)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-400"><X size={16} /></button>
          </div>
          <div><label className={labelCls}>Heading</label><input value={sec.heading || ''} onChange={uSec(i, 'heading')} className={inputCls} /></div>
          <div><label className={labelCls}>Body</label><textarea value={sec.body || ''} onChange={uSec(i, 'body')} rows={4} className={inputCls + ' resize-none'} /></div>
        </div>
      ))}
      <button onClick={addSec} className="flex items-center gap-1 text-sm text-[#76B828] font-medium hover:underline"><Plus size={14} /> Add section</button>
      <div className="flex justify-end"><SaveBtn loading={saving} onClick={() => save(form)} /></div>
    </div>
  );
}

/* ─── Services Editor ─── */
function ServicesEditor({ token }) {
  const { data, loading, saving, save } = useCmsEditor(token, 'services');
  const [form, setForm] = useState(null);

  useEffect(() => { if (data && !form) setForm(JSON.parse(JSON.stringify(data))); }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading || !form) return <div className="flex justify-center py-20"><Loader2 size={28} className="animate-spin text-[#76B828]" /></div>;

  const u = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const uItem = (i, k) => (e) => {
    const items = JSON.parse(JSON.stringify(form.items));
    items[i][k] = e.target.value;
    setForm({ ...form, items });
  };
  const addItem = () => setForm({ ...form, items: [...form.items, { key: '', icon: 'Utensils', title: '', image: '', desc: '' }] });
  const removeItem = (i) => { const items = [...form.items]; items.splice(i, 1); setForm({ ...form, items }); };

  return (
    <div className="space-y-6" data-testid="cms-services-editor">
      <div className="bg-white rounded-2xl border border-black/5 p-6 space-y-4">
        <h3 className="font-display text-lg text-[#0b0b0f]">Services Header</h3>
        <div><label className={labelCls}>Eyebrow</label><input value={form.eyebrow || ''} onChange={u('eyebrow')} className={inputCls} /></div>
        <div><label className={labelCls}>Title</label><input value={form.title || ''} onChange={u('title')} className={inputCls} /></div>
        <div><label className={labelCls}>Description</label><textarea value={form.description || ''} onChange={u('description')} rows={2} className={inputCls + ' resize-none'} /></div>
      </div>
      {(form.items || []).map((item, i) => (
        <div key={i} className="bg-white rounded-2xl border border-black/5 p-6 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-[#8b8b95]">Service {i + 1}</span>
            <button onClick={() => removeItem(i)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-400"><X size={16} /></button>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <div><label className={labelCls}>Title</label><input value={item.title || ''} onChange={uItem(i, 'title')} className={inputCls} /></div>
            <div><label className={labelCls}>Icon name</label><input value={item.icon || ''} onChange={uItem(i, 'icon')} className={inputCls} placeholder="Utensils" /></div>
            <div><label className={labelCls}>Key (slug)</label><input value={item.key || ''} onChange={uItem(i, 'key')} className={inputCls} /></div>
          </div>
          <div><label className={labelCls}>Image URL</label><input value={item.image || ''} onChange={uItem(i, 'image')} className={inputCls} /></div>
          <div><label className={labelCls}>Description</label><textarea value={item.desc || ''} onChange={uItem(i, 'desc')} rows={2} className={inputCls + ' resize-none'} /></div>
        </div>
      ))}
      <button onClick={addItem} className="flex items-center gap-1 text-sm text-[#76B828] font-medium hover:underline"><Plus size={14} /> Add service</button>
      <div className="flex justify-end"><SaveBtn loading={saving} onClick={() => save(form)} /></div>
    </div>
  );
}

/* ─── Features Editor ─── */
function FeaturesEditor({ token }) {
  const { data, loading, saving, save } = useCmsEditor(token, 'features');
  const [form, setForm] = useState(null);

  useEffect(() => { if (data && !form) setForm(JSON.parse(JSON.stringify(data))); }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading || !form) return <div className="flex justify-center py-20"><Loader2 size={28} className="animate-spin text-[#76B828]" /></div>;

  const u = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const uItem = (i, k) => (e) => {
    const items = JSON.parse(JSON.stringify(form.items));
    items[i][k] = e.target.value;
    setForm({ ...form, items });
  };
  const addItem = () => setForm({ ...form, items: [...form.items, { icon: 'Zap', title: '', desc: '' }] });
  const removeItem = (i) => { const items = [...form.items]; items.splice(i, 1); setForm({ ...form, items }); };

  return (
    <div className="space-y-6" data-testid="cms-features-editor">
      <div className="bg-white rounded-2xl border border-black/5 p-6 space-y-4">
        <h3 className="font-display text-lg text-[#0b0b0f]">Product Header</h3>
        <div><label className={labelCls}>Eyebrow</label><input value={form.eyebrow || ''} onChange={u('eyebrow')} className={inputCls} /></div>
        <div><label className={labelCls}>Title</label><input value={form.title || ''} onChange={u('title')} className={inputCls} /></div>
        <div><label className={labelCls}>Description</label><textarea value={form.description || ''} onChange={u('description')} rows={2} className={inputCls + ' resize-none'} /></div>
      </div>
      {(form.items || []).map((item, i) => (
        <div key={i} className="bg-white rounded-2xl border border-black/5 p-6 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-[#8b8b95]">Feature {i + 1}</span>
            <button onClick={() => removeItem(i)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-400"><X size={16} /></button>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <div><label className={labelCls}>Title</label><input value={item.title || ''} onChange={uItem(i, 'title')} className={inputCls} /></div>
            <div><label className={labelCls}>Icon name</label><input value={item.icon || ''} onChange={uItem(i, 'icon')} className={inputCls} placeholder="CalendarClock" /></div>
          </div>
          <div><label className={labelCls}>Description</label><textarea value={item.desc || ''} onChange={uItem(i, 'desc')} rows={2} className={inputCls + ' resize-none'} /></div>
        </div>
      ))}
      <button onClick={addItem} className="flex items-center gap-1 text-sm text-[#76B828] font-medium hover:underline"><Plus size={14} /> Add feature</button>
      <div className="flex justify-end"><SaveBtn loading={saving} onClick={() => save(form)} /></div>
    </div>
  );
}

/* ─── Results Editor ─── */
function ResultsEditor({ token }) {
  const { data, loading, saving, save } = useCmsEditor(token, 'results');
  const [form, setForm] = useState(null);

  useEffect(() => { if (data && !form) setForm(JSON.parse(JSON.stringify(data))); }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading || !form) return <div className="flex justify-center py-20"><Loader2 size={28} className="animate-spin text-[#76B828]" /></div>;

  const u = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const uItem = (i, k) => (e) => {
    const items = JSON.parse(JSON.stringify(form.items));
    items[i][k] = e.target.value;
    setForm({ ...form, items });
  };
  const addItem = () => setForm({ ...form, items: [...form.items, { value: '', suffix: '', note: '' }] });
  const removeItem = (i) => { const items = [...form.items]; items.splice(i, 1); setForm({ ...form, items }); };

  return (
    <div className="space-y-6" data-testid="cms-results-editor">
      <div className="bg-white rounded-2xl border border-black/5 p-6 space-y-4">
        <h3 className="font-display text-lg text-[#0b0b0f]">Results Header</h3>
        <div><label className={labelCls}>Eyebrow</label><input value={form.eyebrow || ''} onChange={u('eyebrow')} className={inputCls} /></div>
        <div><label className={labelCls}>Title</label><input value={form.title || ''} onChange={u('title')} className={inputCls} /></div>
        <div><label className={labelCls}>Description</label><textarea value={form.description || ''} onChange={u('description')} rows={2} className={inputCls + ' resize-none'} /></div>
      </div>
      {(form.items || []).map((item, i) => (
        <div key={i} className="bg-white rounded-2xl border border-black/5 p-6">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-semibold text-[#8b8b95]">Stat {i + 1}</span>
            <button onClick={() => removeItem(i)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-400"><X size={16} /></button>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <div><label className={labelCls}>Value</label><input value={item.value || ''} onChange={uItem(i, 'value')} className={inputCls} placeholder="40" /></div>
            <div><label className={labelCls}>Suffix</label><input value={item.suffix || ''} onChange={uItem(i, 'suffix')} className={inputCls} placeholder="%" /></div>
            <div><label className={labelCls}>Note</label><input value={item.note || ''} onChange={uItem(i, 'note')} className={inputCls} /></div>
          </div>
        </div>
      ))}
      <button onClick={addItem} className="flex items-center gap-1 text-sm text-[#76B828] font-medium hover:underline"><Plus size={14} /> Add stat</button>
      <div className="flex justify-end"><SaveBtn loading={saving} onClick={() => save(form)} /></div>
    </div>
  );
}

/* ─── Account / Change password ─── */
function AccountTab({ token }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const submit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      toast.error('Le nouveau mot de passe doit contenir au moins 8 caract\u00e8res');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    setLoading(true);
    try {
      await axios.post(
        `${API}/api/auth/change-password`,
        { current_password: currentPassword, new_password: newPassword },
        { headers },
      );
      toast.success('Mot de passe mis \u00e0 jour');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erreur lors du changement de mot de passe');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md" data-testid="admin-change-password">
      <div className="bg-white rounded-2xl border border-black/5 p-6 space-y-4">
        <h3 className="font-display text-lg text-[#0b0b0f]">Changer le mot de passe</h3>
        <p className="text-sm text-[#6b6b73]">Utilisez votre mot de passe actuel pour en d\u00e9finir un nouveau (8 caract\u00e8res minimum).</p>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className={labelCls}>Mot de passe actuel</label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className={inputCls}
              required
              autoComplete="current-password"
              data-testid="admin-current-password"
            />
          </div>
          <div>
            <label className={labelCls}>Nouveau mot de passe</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className={inputCls}
              required
              minLength={8}
              autoComplete="new-password"
              data-testid="admin-new-password"
            />
          </div>
          <div>
            <label className={labelCls}>Confirmer le nouveau mot de passe</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className={inputCls}
              required
              minLength={8}
              autoComplete="new-password"
              data-testid="admin-confirm-password"
            />
          </div>
          <button type="submit" disabled={loading} className="btn-pill btn-dark w-full py-3 text-[15px] disabled:opacity-70" data-testid="admin-change-password-btn">
            {loading ? <Loader2 size={18} className="animate-spin mx-auto" /> : 'Mettre \u00e0 jour le mot de passe'}
          </button>
        </form>
      </div>
    </div>
  );
}

/* ─── Dashboard Shell ─── */
const TABS = [
  { key: 'leads', label: 'Leads', icon: LayoutDashboard },
  { key: 'hero', label: 'Hero', icon: Type },
  { key: 'services', label: 'Services', icon: Briefcase },
  { key: 'features', label: 'Product', icon: Grid3x3 },
  { key: 'results', label: 'Results', icon: TrendingUp },
  { key: 'pricing', label: 'Pricing', icon: DollarSign },
  { key: 'faq', label: 'FAQ', icon: HelpCircle },
  { key: 'testimonials', label: 'Testimonials', icon: Quote },
  { key: 'legal', label: 'Legal', icon: Scale },
  { key: 'account', label: 'Compte', icon: KeyRound },
];

function Dashboard({ token, onLogout }) {
  const [tab, setTab] = useState('leads');

  return (
    <div className="min-h-screen bg-[#f8f8fa]">
      <header className="bg-white border-b border-black/5 sticky top-0 z-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 flex h-16 items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-lg bg-[#0b0b0f] flex items-center justify-center">
              <span className="text-white font-extrabold text-lg leading-none">b</span>
            </div>
            <span className="font-display text-lg text-[#0b0b0f]">Admin</span>
          </div>
          <button onClick={onLogout} className="flex items-center gap-2 text-sm text-[#6b6b73] hover:text-[#0b0b0f] transition-colors" data-testid="admin-logout">
            <LogOut size={16} /> {`D\u00e9connexion`}
          </button>
        </div>
      </header>

      {/* Tabs */}
      <div className="border-b border-black/5 bg-white">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 flex gap-1 overflow-x-auto no-scrollbar" data-testid="admin-tabs">
          {TABS.map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors shrink-0 ${
                tab === t.key ? 'border-[#76B828] text-[#76B828]' : 'border-transparent text-[#6b6b73] hover:text-[#0b0b0f]'
              }`}
              data-testid={`tab-${t.key}`}
            >
              <t.icon size={16} /> {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-5 lg:px-8 py-8">
        {tab === 'leads' && <LeadsTab token={token} onLogout={onLogout} />}
        {tab === 'hero' && <HeroEditor token={token} />}
        {tab === 'services' && <ServicesEditor token={token} />}
        {tab === 'features' && <FeaturesEditor token={token} />}
        {tab === 'results' && <ResultsEditor token={token} />}
        {tab === 'pricing' && <PricingEditor token={token} />}
        {tab === 'faq' && <FAQEditor token={token} />}
        {tab === 'testimonials' && <TestimonialsEditor token={token} />}
        {tab === 'legal' && <LegalEditor token={token} />}
        {tab === 'account' && <AccountTab token={token} />}
      </div>
    </div>
  );
}

/* ─── Root ─── */
export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem('bookers_admin_token'));

  const login = (t) => { localStorage.setItem('bookers_admin_token', t); setToken(t); };
  const logout = () => { localStorage.removeItem('bookers_admin_token'); setToken(null); axios.post(`${API}/api/auth/logout`).catch(() => {}); };

  if (!token) return <LoginForm onLogin={login} />;
  return <Dashboard token={token} onLogout={logout} />;
}
